import googleDocsIcon from '@/assets/google-docs-icon.png'
import slackIcon from '@/assets/slack-icon.png'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CopyButton } from '@/components/ui/CopyButton'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import { Cap, type ExternalRef, RefLink } from '../primitives'

/** Channel ids are stable; the display names here are the current ones. */
const DESIGN_SKILLS_LOG: ExternalRef = {
  name: '#insure-design-skills-log',
  href: 'https://cailabsinc.slack.com/archives/C0BQ67UP2LR',
}

const LANGUAGE_CHANNEL: ExternalRef = {
  name: '#insure-language',
  href: 'https://cailabsinc.slack.com/archives/C0BPZLAMP28',
}

const CAI_DESIGN_GUARDRAILS_DOC: ExternalRef = {
  name: 'Cai Design Guardrails',
  href: 'https://docs.google.com/document/d/17UwAm-tNNHqAtDjsz4zz-64d8XLpOYys77RFsFQ1c_U/edit',
}

const CAI_LANGUAGE_DOC: ExternalRef = {
  name: 'Cai Language',
  href: 'https://docs.google.com/document/d/1hpiE8XgSnuJUmjJmk9FpKyDjgLUVcUxGzQkfxMlsYkA/edit',
}

interface Skill {
  /** Anchor id, also used by the sidebar nav. */
  id: string
  command: string
  /** Not rendered while all four skills say the same thing. */
  enabledUsage: string
  accessibleIn: string
  description: string
  supportedInputs: string
  comparesTo: React.ReactNode
  channel: ExternalRef
}

/** Order must match the Skills group in DesignSystemNav.tsx. */
const SKILLS: Skill[] = [
  {
    id: 'skill-cai-design-review',
    command: '/cai-design-review',
    enabledUsage: 'Claude',
    accessibleIn: 'Claude Code',
    description: 'Checks your work against the Design System (this page and code), and suggests fixes.',
    supportedInputs: 'Screenshots, Figma links, code',
    comparesTo: (
      <span className="flex flex-col items-start gap-1">
        <RefLink icon={googleDocsIcon} target={CAI_DESIGN_GUARDRAILS_DOC} />
        <span>Design System (this page and code), components UI</span>
      </span>
    ),
    channel: DESIGN_SKILLS_LOG,
  },
  {
    id: 'skill-cai-prototype',
    command: '/cai-prototype',
    enabledUsage: 'Claude',
    accessibleIn: 'Claude Code',
    description:
      'If using Claude Code, this will publish your work to the prototypes section of the Design System (this page) with context and feedback channels linked.',
    supportedInputs: 'Claude Code created features, webpages, components, etc.',
    comparesTo: <span className="text-ink-400">Not applicable</span>,
    channel: DESIGN_SKILLS_LOG,
  },
  {
    id: 'skill-cai-add-to-design-system',
    command: '/cai-add-to-design-system',
    enabledUsage: 'Claude',
    accessibleIn: 'Claude Code',
    description: 'Adds any new components contained in your work to the Design System (this page).',
    supportedInputs: 'Claude Code created features, webpages, components, etc.',
    comparesTo: 'Design System (this page and code)',
    channel: DESIGN_SKILLS_LOG,
  },
  {
    id: 'skill-cai-language-review',
    command: '/cai-language-review',
    enabledUsage: 'Claude',
    accessibleIn: 'Claude Design, Claude Code',
    description:
      'Reviews customer-facing content against our Cai Language guide, and suggests edits for consistency and clarity.',
    supportedInputs: 'Copy-pasted content, screenshots, Figma links, URLs, code',
    comparesTo: <RefLink icon={googleDocsIcon} target={CAI_LANGUAGE_DOC} />,
    channel: LANGUAGE_CHANNEL,
  },
]

/** One labeled metadata cell in a skill card's detail row. */
function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Cap>{label}</Cap>
      <div className="text-ink mt-2 text-sm">{children}</div>
    </div>
  )
}

/** Label/value row of a skill card's spec table. */
function SpecRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <TableRow>
      <TableCell className="w-52 align-top">
        <Cap>{label}</Cap>
      </TableCell>
      <TableCell className="text-ink align-top whitespace-normal">{children}</TableCell>
    </TableRow>
  )
}

function SkillCard({ skill }: { skill: Skill }) {
  return (
    <Card id={skill.id} className="scroll-mt-10 gap-0 bg-transparent">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className="text-ink font-mono text-xl font-medium">{skill.command}</span>
          <CopyButton
            value={skill.command}
            className="text-ink-400 hover:text-ink size-4 bg-transparent hover:bg-transparent"
          />
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-8">
        <p className="text-md text-ink-500 max-w-2xl leading-relaxed">{skill.description}</p>
        <Table>
          <TableBody>
            <SpecRow label="Supported inputs">{skill.supportedInputs}</SpecRow>
            <SpecRow label="Compares to">{skill.comparesTo}</SpecRow>
            <SpecRow label="Summarizes to">
              <RefLink icon={slackIcon} target={skill.channel} />
            </SpecRow>
          </TableBody>
        </Table>
        <Meta label="Accessible in">{skill.accessibleIn}</Meta>
      </CardContent>
    </Card>
  )
}

export function SkillsSection() {
  return (
    // Unboxed — the individual skill cards carry the structure.
    <section id="skills" className="scroll-mt-10">
      <h2 className="text-ink text-3xl font-extrabold tracking-tight">Skills</h2>
      <div className="mt-10 flex flex-col gap-10">
        {SKILLS.map((skill) => (
          <SkillCard key={skill.id} skill={skill} />
        ))}
      </div>
    </section>
  )
}
